import { useTranslate } from "@refinedev/core";
import { Alert, Button, Card, Flex, Space, Typography } from "antd";
import {
  CaretRightOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import type { TScript, TDetailedSection } from "pages/media/types";
import { useAntTheme } from "hooks/useAntTheme";
import { Script } from "./Script";
import styled from "styled-components";
import { Fragment } from "react";
import { useVolatileBoundStore } from "store";
import useNotification from "antd/es/notification/useNotification";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";

type TDetailedSectionProps = Readonly<{
  changeSection: (section: TDetailedSection) => void;
  generateId: (target: "section") => string;
  generateSection: (topic: string, sectionId: string) => void;
  generateSectionScripts: (
    sectionId: string,
    callback: (scripts: TScript[]) => void
  ) => void;
  improveScript: (
    sectionId: string,
    script: TScript,
    message: string,
    callback: (scripts: TScript[]) => void
  ) => void;
  removeSection: (
    oldSection: TDetailedSection,
    newSection: TDetailedSection
  ) => void;
  section: TDetailedSection;
  editable?: boolean;
  index: number;
  isBusy?: boolean;
}>;

export function Section({
  changeSection,
  generateSection,
  generateSectionScripts,
  improveScript,
  removeSection,
  section,
  editable = true,
  index,
  isBusy = false,
  project,
  storyboardId,
}: TDetailedSectionProps & {
  project?: { id: string };
  storyboardId?: string;
}) {
  // Custom hooks
  const t = useTranslate();
  const { trackEvent } = useAnalytics();

  const handleScriptChange = (index: number, newScript?: Partial<TScript>) => {
    const oldScript = section.scripts?.[index];
    if (!newScript) return;

    const otherScriptsSelected =
      newScript.selected === true && oldScript?.selected !== true
        ? { selected: false }
        : {};

    let updatedScripts: TScript[];

    if (section.scripts?.length) {
      updatedScripts = section.scripts.map((script, indice) => {
        if (index === indice)
          return {
            // merge values
            ...script,
            ...newScript,
          };
        else {
          return { ...script, ...otherScriptsSelected };
        }
      });
    } else {
      // The chapter has no script yet (empty placeholder). Only create a real,
      // selected script once the user types actual content — a blur with an
      // empty value must keep the chapter empty so the warning and the
      // "Generate chapter" button stay visible.
      if (!newScript.content?.trim()) return;
      updatedScripts = [
        {
          ...newScript,
          selected: true,
        } as TScript,
      ];
    }

    const newSection: TDetailedSection = {
      ...section,
      script: updatedScripts.find((script) => script.selected) ?? null,
      scripts: updatedScripts,
    };
    changeSection(newSection);
  };

  return (
    <StyledSection
      styles={{
        header: {
          border: 0,
          padding: 16,
          paddingRight: 0,
          paddingTop: 8,
        },
        body: {
          padding: 0,
        },
      }}
      title={
        <CardTitle
          index={index}
          changeSection={changeSection}
          section={section}
        />
      }
      size="small"
      extra={[
        section.scripts?.length === 0 ? (
          <Button
            key="generate-chapter"
            disabled={!section.topic || !editable}
            size="small"
            shape="round"
            loading={isBusy}
            icon={<CaretRightOutlined />}
            onClick={() =>
              section.topic && generateSection(section.topic, section.id)
            }
          >
            {!section.topic
              ? t("storyboard.editor.Section.addTitle")
              : t("storyboard.editor.Section.generateChapter")}
          </Button>
        ) : null,
        <Space
          size="small"
          key="generate-alternatives-and-delete"
          style={{ marginLeft: 8 }}
        >
          {!!section.scripts?.length && (
            <Button
              disabled={!section.topic || !editable}
              loading={isBusy}
              size="small"
              shape="round"
              onClick={() => {
                generateSectionScripts(section.id, (scripts: TScript[]) => {
                  // Keep the current script as the default choice and append
                  // the freshly generated alternatives, so the user never
                  // silently loses their existing script. They can still
                  // switch to an alternative if they prefer one.
                  const currentScript =
                    section.scripts?.find((script) => script.selected) ??
                    section.script;

                  const mergedScripts: TScript[] = currentScript
                    ? [
                        { ...currentScript, selected: true },
                        ...scripts.map((script) => ({
                          ...script,
                          selected: false,
                        })),
                      ]
                    : scripts;

                  const newSection: TDetailedSection = {
                    ...section,
                    script:
                      mergedScripts.find((script) => script.selected) ?? null,
                    scripts: mergedScripts,
                  };
                  changeSection(newSection);
                });
              }}
              icon={<CaretRightOutlined />}
            >
              {t("storyboard.editor.Section.generateAlternatives")}
            </Button>
          )}
          <Button
            danger
            key="delete"
            size="middle"
            shape="circle"
            icon={<DeleteOutlined />}
            onClick={() => {
              trackEvent(AnalyticsEvents.StoryboardSectionDelete, {
                project_id: project?.id,
                storyboard_id: storyboardId,
                storyboard_section_index: index,
              });
              removeSection(section, {
                scripts: [],
                script: {
                  content: "",
                  selected: false,
                },
                topic: "",
                id: section.id,
              });
            }}
            type="text"
          />
        </Space>,
      ]}
    >
      {section.scripts?.length === 0 ? (
        // Empty chapter: warn that a script must be added or generated, instead
        // of rendering the guidance text as if it were a real (saved) script.
        <Alert
          showIcon
          style={{
            display: "inline-flex",
            marginBottom: 16,
            marginLeft: 16,
            padding: "2px 10px",
            fontSize: 12,
          }}
          type="warning"
          message={t("storyboard.editor.Section.addText")}
        />
      ) : (
        !section.script && (
          <Alert
            showIcon
            style={{ display: "inline-flex", marginBottom: 16, marginLeft: 16 }}
            type="warning"
            message={t("storyboard.editor.Section.noFinalScript")}
          />
        )
      )}
      <Card
        size="small"
        variant="borderless"
        style={{
          flex: 1,
          border: "none",
          background: "none",
          boxShadow: "none",
        }}
        styles={{
          body: { padding: 0 },
          header: {
            alignContent: "flex-end",
            border: 0,
            paddingLeft: 0,
            paddingTop: 5,
            minHeight: 0,
          },
        }}
      >
        {section.scripts?.map((script, sectionIndex) => {
          return (
            script && (
              <Fragment key={sectionIndex}>
                <Script
                  index={sectionIndex}
                  sectionId={section.id}
                  changeScript={handleScriptChange}
                  improveScript={improveScript}
                  script={script}
                  editable={editable}
                  showPrompt={!section.id.startsWith("section")} // disable prompt if not generated
                  // Only allow choosing once there are alternatives to pick from
                  showSelect={(section.scripts?.length ?? 0) > 1}
                  isBusy={isBusy}
                />
              </Fragment>
            )
          );
        })}
        {section.scripts?.length === 0 && (
          <Script
            index={0}
            sectionId={section.id}
            changeScript={handleScriptChange}
            improveScript={improveScript}
            script={{
              content: "",
              selected: false,
            }}
            editable={editable}
            showPrompt={false}
            isBusy={isBusy}
          />
        )}
      </Card>
    </StyledSection>
  );
}

export const CardTitle = ({
  index,
  changeSection,
  section,
}: {
  index: number;
  changeSection: (section: TDetailedSection) => void;
  section: TDetailedSection;
}) => {
  const { storyboard } = useVolatileBoundStore((store) => ({
    storyboard: store.storyboardState.storyboard,
  }));

  const t = useTranslate();
  const { theme } = useAntTheme();

  const [api, contextHolder] = useNotification();
  return (
    <Flex gap={15}>
      {contextHolder}

      <Typography.Text
        style={{
          fontSize: "1.3em",
        }}
      >
        {index + 1}.{" "}
      </Typography.Text>
      <Typography.Text
        style={{
          padding: 0,
          margin: 0,
          background: "none",
          fontSize: "1.3em",
          width: "100%",
          whiteSpace: "normal",
          // override default antd styles
          // input is cropped from the top without this
          insetBlockStart: 0,
        }}
        editable={{
          enterIcon: null,
          icon: <EditOutlined style={{ color: theme.colorPrimary }} />,
          triggerType: ["text", "icon"],
          onChange: (value) => {
            if (!value || value === section.topic) return;

            const topicExists = storyboard?.sections.some(
              (s) => s.topic === value && s.id !== section.id
            );

            if (topicExists) {
              api.warning({
                message: t("storyboard.editor.Section.chapterExists"),
              });
              return;
            }
            changeSection({
              ...section,
              topic: value,
            });
          },
        }}
      >
        {section.topic}
      </Typography.Text>
    </Flex>
  );
};

const StyledSection = styled(Card)`
  background: none;
  border: none;
`;
